import {
  Anchor,
  Button,
  Icon,
  ListItem,
  TabbedDrawer,
  H4,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerTab,
  TabbedDrawerCTA,
} from "@sellar-io/components";
import { capitalize, pluralize } from "@sellar-io/common";
import { useCustomerZonesUpdate } from "../../../hooks/api/v2/customerZones/useCustomerZonesUpdate";
import { useState } from "react";
import { Alert } from "../../../components/alert/Alert";
import { InputLabel } from "@sellar-io/components";
import { useNavigate } from "react-router";
import * as ROUTES from "../../../constants/routes";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";

import { twMerge } from "tailwind-merge";
import { useFulfillmentTypeList } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { FulfillmentTypeListResponseType } from "@sellar-io/api-v2/types";
import { DeliveryMethodContent } from "../../delivery/DeliveryMethod";

export const CustomerZoneDeliveryMethods = ({
  customerZoneId,
  customerZoneType,
  currentDrayDeliveryMethodId,
  currentCourierDeliveryMethodId,
  currentPalletDeliveryMethodId,
}: {
  customerZoneId: number;
  customerZoneType: "direct" | "thirdParty";
  currentDrayDeliveryMethodId?: number | null;
  currentCourierDeliveryMethodId?: number | null;
  currentPalletDeliveryMethodId?: number | null;
}) => {
  const [error, setError] = useState<string>();
  const [sideDrawerActive, setSideDrawerActive] = useState(false);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
    useState<FulfillmentTypeListResponseType[number]>();

  const drayDeliveryMethodsQuery = useFulfillmentTypeList({
    query: [
      {
        type: "FulfillmentType",
        modifier: "EQUALS",
        value: "direct",
      },
    ],
    limit: 10,
  });

  const drayDeliveryMethods = drayDeliveryMethodsQuery.data?.pages.flat() ?? [];

  const courierDeliveryMethodsQuery = useFulfillmentTypeList({
    query: [
      {
        type: "FulfillmentType",
        modifier: "EQUALS",
        value: "courier",
      },
    ],
    limit: 10,
  });

  const courierDeliveryMethods =
    courierDeliveryMethodsQuery.data?.pages.flat() ?? [];

  const palletDeliveryMethodsQuery = useFulfillmentTypeList({
    query: [
      {
        type: "FulfillmentType",
        modifier: "EQUALS",
        value: "haulier",
      },
    ],
    limit: 10,
  });

  const palletDeliveryMethods =
    palletDeliveryMethodsQuery.data?.pages.flat() ?? [];

  const customerZoneUpdate = useCustomerZonesUpdate(customerZoneId);

  const updateCustomerZone = (data: {
    DrayDeliveryMethodId?: number | null;
    CourierDeliveryMethodId?: number | null;
    PalletDeliveryMethodId?: number | null;
  }) => {
    customerZoneUpdate.mutate(data, {
      onError: () => {
        setError(`Failed to update customer zone with that delivery method`);
      },
    });
  };

  return (
    <>
      <div className="flex flex-col gap-4 px-4">
        {error && (
          <Alert type="error" title="Failed to update" message={error} />
        )}
        <div className="flex flex-col gap-1 border-b border-zinc-300 pb-4">
          <H4 className="font-semibold">Delivery methods</H4>
          <p className="text-zinc-500">
            Choose the delivery methods your customers will see on your
            storefront. For more information or help setting up your delivery
            methods, visit our{" "}
            <Anchor
              className="inline-flex items-center gap-1 text-blue-600 underline"
              href="https://intercom.help/sellario/en/articles/12008469-supplier-guide-product-catalogues"
              target="_blank"
              rel="noopener noreferrer"
            >
              help centre
              <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
            </Anchor>
            .
          </p>
        </div>

        {customerZoneType === "direct" && (
          <>
            <InputLabel>Direct</InputLabel>
            <InfiniteList
              query={drayDeliveryMethodsQuery}
              className="flex flex-col gap-2"
            >
              {drayDeliveryMethods.map((drayDeliveryMethod) => {
                const selected =
                  drayDeliveryMethod.id === currentDrayDeliveryMethodId;

                return (
                  <ListItem
                    key={drayDeliveryMethod.id}
                    title={drayDeliveryMethod.name ?? "Direct"}
                    subtitle={""}
                    icon={selected ? "CHECK" : "TRUCK"}
                    actionText="Preview"
                    onClick={() => {
                      setSelectedDeliveryMethod(drayDeliveryMethod);
                      setSideDrawerActive(true);
                    }}
                    iconClick={() =>
                      updateCustomerZone({
                        DrayDeliveryMethodId: selected
                          ? null
                          : drayDeliveryMethod.id,
                      })
                    }
                    className={twMerge(
                      "rounded-lg bg-white p-4 hover:bg-zinc-50",
                      selected ? "border border-zinc-300 bg-zinc-50" : "",
                    )}
                    iconClassName={
                      selected ? "border-zinc-800 bg-zinc-200" : ""
                    }
                  />
                );
              })}
            </InfiniteList>
          </>
        )}

        <InputLabel>Courier</InputLabel>
        <InfiniteList
          query={courierDeliveryMethodsQuery}
          className="flex flex-col gap-2"
        >
          {courierDeliveryMethods.map((courierDeliveryMethod) => {
            const selected =
              courierDeliveryMethod.id === currentCourierDeliveryMethodId;

            return (
              <ListItem
                key={courierDeliveryMethod.id}
                title={courierDeliveryMethod.name ?? "Courier"}
                subtitle={""}
                icon={selected ? "CHECK" : "PACKAGE_VARIANT_CLOSED_FILL"}
                actionText="Preview"
                onClick={() => {
                  setSelectedDeliveryMethod(courierDeliveryMethod);
                  setSideDrawerActive(true);
                }}
                iconClick={() =>
                  updateCustomerZone({
                    CourierDeliveryMethodId: selected
                      ? null
                      : courierDeliveryMethod.id,
                  })
                }
                className={twMerge(
                  "rounded-lg bg-white p-4 hover:bg-zinc-50",
                  selected ? "border border-zinc-300 bg-zinc-50" : "",
                )}
                iconClassName={selected ? "border-zinc-800 bg-zinc-200" : ""}
              />
            );
          })}
        </InfiniteList>

        <InputLabel>Pallet</InputLabel>
        <InfiniteList
          query={palletDeliveryMethodsQuery}
          className="mb-8 flex flex-col gap-2"
        >
          {palletDeliveryMethods.map((palletDeliveryMethod) => {
            const selected =
              palletDeliveryMethod.id === currentPalletDeliveryMethodId;

            return (
              <ListItem
                key={palletDeliveryMethod.id}
                title={palletDeliveryMethod.name ?? "Haulier"}
                subtitle={""}
                icon={selected ? "CHECK" : "PALLET"}
                actionText="Preview"
                onClick={() => {
                  setSelectedDeliveryMethod(palletDeliveryMethod);
                  setSideDrawerActive(true);
                }}
                iconClick={() =>
                  updateCustomerZone({
                    PalletDeliveryMethodId: selected
                      ? null
                      : palletDeliveryMethod.id,
                  })
                }
                className={twMerge(
                  "rounded-lg bg-white p-4 hover:bg-zinc-50",
                  selected ? "border border-zinc-300 bg-zinc-50" : "",
                )}
                iconClassName={selected ? "border-zinc-800 bg-zinc-200" : ""}
              />
            );
          })}
        </InfiniteList>
      </div>
      <DeliveryMethodPreview
        customerZoneId={customerZoneId}
        active={sideDrawerActive}
        setActive={(value: boolean) => {
          setSideDrawerActive(value);
          if (!sideDrawerActive) setSelectedDeliveryMethod(undefined);
        }}
        deliveryMethod={selectedDeliveryMethod}
        assign={() => {
          if (selectedDeliveryMethod?.id) {
            updateCustomerZone({
              ...(selectedDeliveryMethod.fulfillmentType === "direct"
                ? { DrayDeliveryMethodId: selectedDeliveryMethod.id }
                : {}),
              ...(selectedDeliveryMethod.fulfillmentType === "courier"
                ? { CourierDeliveryMethodId: selectedDeliveryMethod.id }
                : {}),
              ...(selectedDeliveryMethod.fulfillmentType === "haulier"
                ? { PalletDeliveryMethodId: selectedDeliveryMethod.id }
                : {}),
            });
          }
          setSideDrawerActive(false);
          if (!sideDrawerActive) setSelectedDeliveryMethod(undefined);
        }}
      />
    </>
  );
};

const DeliveryMethodPreview = ({
  customerZoneId,
  active,
  setActive,
  deliveryMethod,
  assign,
}: {
  customerZoneId: number;
  active: boolean;
  setActive: (value: boolean) => void;
  deliveryMethod?: FulfillmentTypeListResponseType[number];
  assign: () => void;
}) => {
  return (
    <TabbedDrawer active={active} setActive={setActive} initTab={0}>
      {active && deliveryMethod && (
        <Drawer
          customerZoneId={customerZoneId}
          deliveryMethod={deliveryMethod}
          assign={assign}
        />
      )}
    </TabbedDrawer>
  );
};

const Drawer = ({
  customerZoneId,
  deliveryMethod,
  assign,
}: {
  customerZoneId: number;
  deliveryMethod: FulfillmentTypeListResponseType[number];
  assign: () => void;
}) => {
  const navigate = useNavigate();

  if (!deliveryMethod) return <></>;

  const numberOfCustomers = 0;

  return (
    <>
      <TabbedDrawerHeader>
        <TabbedDrawerTitle
          title={
            deliveryMethod.name ?? capitalize(deliveryMethod.fulfillmentType)
          }
          subtitle={`Assigned to ${numberOfCustomers} ${pluralize("customer", numberOfCustomers)}`}
        />
      </TabbedDrawerHeader>
      <TabbedDrawerTab tab={0}>
        <DeliveryMethodContent
          deliveryMethod={deliveryMethod}
          readOnly={true}
        />
      </TabbedDrawerTab>
      <TabbedDrawerCTA className="flex-row">
        <Button
          className="flex w-full items-center gap-1"
          color="transparent"
          onClick={() =>
            navigate(
              ROUTES.DELIVERY_METHOD.href({
                deliveryMethodId: deliveryMethod.id,
              }),
              {
                state: { customerZoneRedirectId: customerZoneId },
              },
            )
          }
        >
          <Icon icon="PENCIL" />
          Edit
        </Button>
        <Button className="w-full" onClick={assign}>
          Assign
        </Button>
      </TabbedDrawerCTA>
    </>
  );
};
